import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Lock, LogOut, BookOpen, GraduationCap, Award, Library, Tags } from 'lucide-react';
import api, { formatApiErrorDetail } from '@/lib/adminApi';
import { ResourceManager } from '@/pages/admin/ResourceManager';
import { resourceConfigs } from '@/pages/admin/resourceConfigs';

const tabIcons = { blogs: BookOpen, universities: GraduationCap, scholarships: Award, courses: Library, categories: Tags };

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      localStorage.setItem('admin_token', data.access_token);
      onLogin(data.user);
      toast.success('Welcome back!');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center bg-white py-12">
      <div className="w-full max-w-md border border-border/40 rounded-sm p-8 bg-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-sm bg-primary flex items-center justify-center">
            <Lock className="text-primary-foreground" size={18} />
          </div>
          <div>
            <h1 className="text-2xl font-playfair font-bold">Admin Login</h1>
            <p className="text-sm text-muted-foreground">InsideHigherEducation CMS</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="admin-email">Email</Label>
            <Input id="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-sm" data-testid="admin-login-email" />
          </div>
          <div>
            <Label htmlFor="admin-password">Password</Label>
            <Input id="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="rounded-sm" data-testid="admin-login-password" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full rounded-sm" data-testid="admin-login-submit">
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default function AdminPage() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { setUser(null); return; }
    api.get('/auth/me')
      .then(({ data }) => setUser(data))
      .catch(() => { localStorage.removeItem('admin_token'); setUser(null); });
  }, []);

  const logout = () => {
    localStorage.removeItem('admin_token');
    setUser(null);
    toast.success('Logged out');
  };

  if (user === undefined) {
    return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Loading...</div>;
  }
  if (!user) return <AdminLogin onLogin={setUser} />;

  return (
    <div className="py-12 bg-white" data-testid="admin-dashboard">
      <div className="container mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-playfair font-bold text-foreground mb-2">Admin CMS</h1>
            <p className="text-lg text-muted-foreground">Signed in as {user.email}</p>
          </div>
          <Button variant="outline" onClick={logout} className="rounded-sm" data-testid="admin-logout-btn">
            <LogOut className="mr-2" size={16} /> Logout
          </Button>
        </div>

        <Tabs defaultValue="blogs" className="w-full">
          <TabsList className="grid w-full grid-cols-5 mb-8">
            {resourceConfigs.map((config) => {
              const Icon = tabIcons[config.key];
              return (
                <TabsTrigger key={config.key} value={config.key} data-testid={`tab-${config.key}`}>
                  <Icon className="mr-2 hidden sm:inline" size={16} />
                  {config.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
          {resourceConfigs.map((config) => (
            <TabsContent key={config.key} value={config.key}>
              <ResourceManager config={config} />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
